import { HttpBackend, HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, Subject, Subscriber } from "rxjs";

import { UntilDestroy } from "@ngneat/until-destroy";

import {
  ChangePasswordModel,
  ForgotPasswordModel,
  LoginModel,
  RegisterModel,
  ResetPasswordModel,
} from "./auth-models";
import { AuthUser } from "./auth-user";
import { splitPermissions } from "./permissions/permissions-utility";
import { PermissionValue } from "./permissions/permissions";
import { NotificationService } from "../shared/services/notification.service";

const DEFAULT_LOGOUT_REDIRECT_URL = "/auth/login";

interface LogoutResponse {
  redirectUrl: string;
}

enum ClaimTypes {
  UserType = "UserType",
  AdministrativeUnitId = "AdministrativeUnitId",
  AdministrativeUnitName = "AdministrativeUnitName",
  OrganizationId = "OrganizationId",
  SchoolYear = "SchoolYear",
  UserAdministrativeUnitRole = "UserAdministrativeUnitRole",
  ParentSurveyPermission = "ParentSurveyPermissions",
}

enum UserTypes {
  StateEducationAgency = "StateEducationAgency",
  AdministrativeUnit = "AdministrativeUnit",
  District = "District",
  Family = "Family",
  Provider = "Provider",
  School = "School",
  Participant = "Participant",
  Teacher = "Teacher",
  Public = "Public",
}

@UntilDestroy({ checkProperties: true })
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private user: AuthUser;
  get authUser(): AuthUser {
    return this.user;
  }
  private set thisUser(v: AuthUser) {
    this.user = v;
    this.publishAuthUserChanged();
  }

  redirectUrl = "";
  private nonInterceptedHttpClient: HttpClient;

  private authUserChangeSource = new BehaviorSubject<AuthUser>(null);
  authUserChanged$ = this.authUserChangeSource.asObservable();

  private caseLookup: { [learnerId: string]: string } = {};

  constructor(
    private notificationService: NotificationService,
    private http: HttpClient,
    private router: Router,
    private handler: HttpBackend
  ) {
    this.nonInterceptedHttpClient = new HttpClient(handler);
  }

  get isStateEducationAgencyUser() {
    return !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserType &&
        c.value === UserTypes.StateEducationAgency
    );
  }

  get isPublicUser() {
    return !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserType &&
        c.value === UserTypes.Public
    );
  }

  get isAdministrativeUnitUser() {
    return !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserType &&
        c.value === UserTypes.AdministrativeUnit
    );
  }

  get isDistrictUser() {
    return !!this.authUser?.claims.find(
      (c) => c.type === ClaimTypes.UserType && c.value === UserTypes.District
    );
  }

  get isCDEAdminUser() {
    const hasStateEducationAgencyType = !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserType &&
        c.value === UserTypes.StateEducationAgency
    );

    const hasAdminRole = !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserAdministrativeUnitRole &&
        typeof c.value === 'string' && c.value.toUpperCase() === 'ADMIN'
    );

    return hasStateEducationAgencyType && hasAdminRole;
  }

  get isCDEUser() {
    const hasStateEducationAgencyType = !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserType &&
        c.value === UserTypes.StateEducationAgency
    );

    const hasContractorRole = !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserAdministrativeUnitRole &&
        typeof c.value === 'string' && (c.value.toUpperCase().includes('CON') || c.value.toUpperCase().includes('LIM'))
    );

    return hasStateEducationAgencyType && !hasContractorRole;
  }

  get isCDEContractorUser() {
    const hasStateEducationAgencyType = !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserType &&
        c.value === UserTypes.StateEducationAgency
    );

    const hasContractorRole = !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserAdministrativeUnitRole &&
        typeof c.value === 'string' && (c.value.toUpperCase().includes('CON') || c.value.toUpperCase().includes('LIM'))
    );

    return hasStateEducationAgencyType && hasContractorRole;
  }

  get isAbleToAlternateParentSurveyRecords() {
    return !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.ParentSurveyPermission &&
        c.value === '3' //ParentSurveyPermissions.EditSurvey
    );
  }

  get userRoles() {
    return this.authUser?.claims
      .filter((c) => c.type === ClaimTypes.UserAdministrativeUnitRole)
      .map((m) => m.value)
      .join(", "); //must join like this so the string is compatible with the user roles selection UI
  }

  get isSchoolLevelUser() {
    return !!this.authUser?.claims.find(
      (c) =>
        c.type === ClaimTypes.UserType &&
        (c.value === UserTypes.Family ||
          c.value === UserTypes.Participant ||
          c.value === UserTypes.Provider ||
          c.value === UserTypes.School ||
          c.value === UserTypes.Teacher)
    );
  }

  get isAuthenticated() {
    return !!this.authUser;
  }

  get administrativeUnitId() {
    return this.authUser?.claims.find((c) => c.type === ClaimTypes.AdministrativeUnitId)
      ?.value;
  }

  get organizationId() {
    return this.authUser?.claims.find(
      (c) => c.type === ClaimTypes.OrganizationId
    )?.value as string;
  }

  // TODO: school dropdown selector
  // get schoolId() {
  //   return null;
  // }
  schoolId: string;

  //schoolYear: string;
  get schoolYear() {
    return this.authUser?.claims.find((c) => c.type === ClaimTypes.SchoolYear)
      ?.value as string;
  }
  set schoolYear(value: string) {
    //check to make sure the user and the claim exists, then update
    if (
      this.authUser &&
      this.authUser?.claims.find((c) => c.type === ClaimTypes.SchoolYear)
    ) {
      this.authUser.claims.find((c) => c.type === ClaimTypes.SchoolYear).value =
        value;
    }
  }

  publishAuthUserChanged() {
    this.authUserChangeSource.next(this.user);
  }

  login(creds: LoginModel): Observable<AuthUser> {
    this.thisUser = null;
    return new Observable<AuthUser>((sub: Subscriber<AuthUser>) => {
      this.http
        .post<AuthUser>("/api/login", creds)
        .subscribe(this.processAuthUser(sub), (err: unknown) => {
          this.thisUser = null;
          sub.error(err);
        });
    });
  }

  selectAdministrativeUnit(administrativeUnitId: string, schoolYear: string): Observable<AuthUser> {
    return new Observable<AuthUser>((sub: Subscriber<AuthUser>) => {
      this.http
        .put<AuthUser>(`/api/select-tenant/${administrativeUnitId}/${schoolYear}`, null)
        .subscribe(this.processAuthUser(sub));
    });
  }

  register(creds: RegisterModel): Observable<AuthUser> {
    this.thisUser = null;
    return new Observable<AuthUser>((sub: Subscriber<AuthUser>) => {
      this.http
        .post<AuthUser>("/api/register", creds)
        .subscribe(this.processAuthUser(sub), (err: unknown) => {
          this.thisUser = null;
          sub.error(err);
        });
    });
  }

  navigateToRootUrl() {
    this.router.navigate(["/"]);
    this.notificationService.alert("Sorry! You don't have permission to access this. Please contact your admin");
  }

  navigateToLogin() {
    this.router.navigate(["auth", "login"]);
  }

  navigateToSelectDistrict() {
    this.router.navigate(["auth", "select-district"]);
  }

  logout(redirectUrl?: string) {
    this.thisUser = null;
    this.caseLookup = {};
    this.redirectUrl = "";
    this.http.post<LogoutResponse>("/api/logout", {}).subscribe(
      // The server owns the post-logout destination so it can vary per environment
      // (each environment federates with its own IDCS tenant). An explicit caller
      // supplied url still wins, and a failed request must not strand the user in a
      // signed out app, so both fall back to the local login page.
      (res) =>
        this.redirectTo(
          redirectUrl || res?.redirectUrl || DEFAULT_LOGOUT_REDIRECT_URL
        ),
      () => this.redirectTo(redirectUrl || DEFAULT_LOGOUT_REDIRECT_URL)
    );
  }

  /** Full page navigation, isolated so sign out can be asserted without a real redirect. */
  redirectTo(url: string) {
    window.location.href = url;
  }

  navigateToLoginAndReload() {
    this.router
      .navigate(["auth", "login"])
      .finally(() => window.location.reload());
  }

  authenticate(redirectUrl = "") {
    this.thisUser = null;
    this.redirectUrl = redirectUrl;
    return new Observable<AuthUser>((sub: Subscriber<AuthUser>) => {
      this.http
        .get<AuthUser>("/api/auth")
        .subscribe(this.processAuthUser(sub), (err: unknown) => sub.error(err));
    });
  }

  nonInterceptedAuthenticate(redirectUrl = "") {
    this.thisUser = null;
    this.redirectUrl = redirectUrl;
    return new Observable<AuthUser>((sub: Subscriber<AuthUser>) => {
      this.nonInterceptedHttpClient
        .get<AuthUser>("/api/auth")
        .subscribe(this.processAuthUser(sub), (err: unknown) => sub.error(err));
    });
  }

  navigatePostAuth() {
    this.router.navigate([this.redirectUrl]);
    this.redirectUrl = "";
  }

  private processAuthUser(sub: Subscriber<AuthUser>) {
    return (user: AuthUser) => {
      this.thisUser = user;
      sub.next(user);
      sub.complete();
    };
  }

  getPermissions<T>(permissionsCategory: new (...args: any[]) => T) {
    const claim = this.authUser?.claims?.find(
      (x) => x.type === permissionsCategory.name
    )?.value;
    const permissions =
      typeof claim !== "undefined" && claim !== null
        ? parseInt(claim as string)
        : 0;
    return splitPermissions(permissionsCategory, permissions);
  }

  getPermission(required: PermissionValue | number) {
    // The @PermissionsCategory decorator transforms static number properties
    // to PermissionValue objects at runtime, but TypeScript still sees them as number.
    const pv = required as PermissionValue;
    const claim = this.authUser?.claims?.find(
      (x) => x.type === pv.type
    )?.value;
    const permission =
      typeof claim !== "undefined" && claim !== null
        ? parseInt(claim as string)
        : 0;
    return (permission & pv.val) === pv.val;
  }

  forgotPassword(body: ForgotPasswordModel) {
    return this.http.post("/api/forgotPassword", body);
  }

  resetPassword(body: ResetPasswordModel) {
    return this.http.post("/api/reset-password", body);
  }

  changePassword(body: ChangePasswordModel) {
    return this.http.post("/api/change-password", body);
  }
}
